import React from 'react';
import { Trash2, AlertCircle, Mic } from 'lucide-react';
import SimpleTranscribe from './SimpleTranscribe';
import useTranscription from '../hooks/useTranscription';

/**
 * SimpleTranscribePage Component
 * 
 * A clean, minimal page for speech transcription with:
 * - Text area showing transcribed text
 * - Simple transcribe component (mic button)
 * - Clear button to reset transcription
 * - Error handling and status display
 * 
 * Follows KISS principles with a focus on usability and maintainability.
 */
const SimpleTranscribePage = () => {
  const {
    transcribedText,
    isRecording,
    error,
    appendText,
    clearText,
    setRecordingState,
    setError,
    clearError
  } = useTranscription();

  const handleTranscriptionUpdate = (newText) => {
    appendText(newText);
    clearError(); // Clear any previous errors when receiving new text
  };

  const handleError = (err) => {
    setError(err);
  };

  const handleRecordingStateChange = (recording) => {
    setRecordingState(recording);
    if (recording) {
      clearError(); // Clear errors when starting new recording
    }
  };

  const handleClear = () => {
    clearText();
    clearError();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-blue-800 pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl mb-4">
              <Mic className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Simple Transcription
            </h1>
            <p className="text-xl text-blue-100">
              Click the mic to start recording, click again to stop
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 mb-6 backdrop-blur-sm">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                <span className="text-red-200">{error}</span>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            {/* Controls Section */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                {/* Transcribe Component */}
                <SimpleTranscribe
                  onTranscriptionUpdate={handleTranscriptionUpdate}
                  onError={handleError}
                  onRecordingStateChange={handleRecordingStateChange}
                  size="lg"
                />
                
                {/* Status Indicator */}
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    isRecording ? 'bg-red-400 animate-pulse' : 'bg-gray-400'
                  }`} />
                  <span className={`text-sm font-medium ${
                    isRecording ? 'text-red-300' : 'text-gray-300'
                  }`}>
                    {isRecording ? 'Recording...' : 'Ready'}
                  </span>
                </div>
              </div>

              {/* Clear Button */}
              <button
                onClick={handleClear}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50"
                title="Clear transcription"
              >
                <Trash2 className="w-4 h-4" />
                <span>Clear</span>
              </button>
            </div>

            {/* Transcription Text Area */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white">
                Transcribed Text
              </label>
              <textarea
                value={transcribedText}
                readOnly
                placeholder={
                  isRecording 
                    ? "Listening... Start speaking!" 
                    : "Click the microphone to start recording..."
                }
                className="w-full h-64 p-4 bg-black/20 border border-white/10 rounded-xl text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                style={{ fontFamily: 'monospace' }}
              />
              
              {/* Character Count */}
              <div className="flex justify-between text-sm text-gray-400">
                <span>
                  {transcribedText.length} characters
                </span>
                <span>
                  {transcribedText.trim().split(/\s+/).filter(word => word.length > 0).length} words
                </span>
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <h3 className="text-sm font-medium text-blue-300 mb-2">How to use:</h3>
              <ul className="text-sm text-blue-200 space-y-1">
                <li>• Click the green microphone to start recording</li>
                <li>• The microphone turns red while recording</li>
                <li>• Click the red microphone to stop recording</li>
                <li>• Transcribed text will appear in the text area</li>
                <li>• Use the Clear button to reset the transcription</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleTranscribePage;
